import {React, useState, useEffect} from 'react';
import '../../App.css'
import { Grid, Box, Button, Paper, Stack, FormControl, InputLabel, Select, MenuItem, FormHelperText, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab } from '@mui/material';
import { Summarize} from '@mui/icons-material';
import * as monthList from '../../services/monthList';
import * as yearList from '../../services/yearList';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Notification from '../Notification';
import * as XLSX from 'xlsx-js-style';
import { getStoredCompanyDetails, getCompanyDisplayFromDetails, getShortCompanyName } from '../../services/companyService';
import { setLoaderMessage } from '../../utils/loaderManager';

const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red' , marginLeft: '3px'}}>*</span>
  </span>
);

const monthOptions = monthList.getMonthList();
const yearOptions = yearList.getYearList();

const today = new Date();
const currentYear = today.getFullYear().toString();
const currentMonthIndex = today.getMonth();
const currentMonthName = monthOptions[currentMonthIndex].title;

const initialFValues={
  id: 0,
  month: currentMonthName,
  year: currentYear,
}

function BankStatementReport({ isEmbedded = false }){
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [empDetails, setEmpDetails] = useState([]); // Array of arrays: [[employees...], [employees...]]
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [notify, setNotify] = useState({isOpen: false, message: '', type: ''});
  const [openModal, setOpenModal] = useState(false);
  const [creditNarration, setCreditNarration] = useState('');
  const [debitNarration, setDebitNarration] = useState('');
  const [activeEmployeeTab, setActiveEmployeeTab] = useState('icici');
  const [currentTableIndex, setCurrentTableIndex] = useState(null); // Track which table is being generated
  const [generatedTables, setGeneratedTables] = useState(new Set()); // Track which tables have been generated
  const [generatedEmployeeIds, setGeneratedEmployeeIds] = useState(new Set()); // Track which employee IDs have been generated
  const navigate = useNavigate();
  const companyDetails = useSelector((state) => state.auth.activeCompanyDetails) || getStoredCompanyDetails();
  const { companyName: globalCompanyName, addressLine1: globalAddressLine1, addressLine2: globalAddressLine2 } = getCompanyDisplayFromDetails(companyDetails || {});

  // Helper function to clean bank account numbers
  const cleanDisplayValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    let cleaned = String(value);
    cleaned = cleaned.replace(/'/g, '');
    cleaned = cleaned.replace(/[''""]/g, '');
    cleaned = cleaned.trim();
    return cleaned;
  };

  // Helper function to get last 4 digits of account number
  const getLast4Digits = (accountNumber) => {
    if (!accountNumber) return '0000';
    const cleanedAccount = cleanDisplayValue(accountNumber);
    return cleanedAccount.slice(-4).padStart(4, '0');
  };

  // Helper function to get first 4 digits of account number
  const getFirst4Digits = (accountNumber) => {
    if (!accountNumber) return '0000';
    const cleanedAccount = cleanDisplayValue(accountNumber);
    return cleanedAccount.substring(0, 4).padEnd(4, '0');
  };

  // Helper function to pad strings for alignment (truncate if too long)
  const padRight = (str, length) => {
    const strValue = String(str || '');
    // Truncate if longer than specified length
    const truncated = strValue.length > length ? strValue.substring(0, length) : strValue;
    // Pad to the right with spaces
    return truncated.padEnd(length, ' ');
  };

  // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
  const getOrdinalSuffix = (num) => {
    if (num === 1) return '1st';
    if (num === 2) return '2nd';
    if (num === 3) return '3rd';
    return `${num}th`;
  };

  const getUploadCounterStorageKey = (month, year) => {
    const monthAbbr = month.substring(0, 3);
    const yearShort = String(year).slice(-2);
    return `bank_statement_counter_${monthAbbr}${yearShort}`;
  };

  const consumeUploadCounter = (month, year) => {
    const storageKey = getUploadCounterStorageKey(month, year);
    const counter = parseInt(localStorage.getItem(storageKey) || '1', 10);
    localStorage.setItem(storageKey, (counter + 1).toString());
    return counter;
  };

  const getIciciSalaryUploadExcelFilename = (companyName, month, year, uploadCounter) => {
    const reportCompanyName = getShortCompanyName(companyName) || 'Report';
    return `${reportCompanyName} ${getOrdinalSuffix(uploadCounter)} salary upload for ICICI ${month.substring(0, 3)} ${year}.xlsx`;
  };

  // Download notepad file
  const downloadNotepadFile = (reportData, month, year, creditNarration, debitNarration, uploadCounter) => {
    const company_name = reportData?.company_name || '';
    const bank_account_number = reportData?.bank_account_number || null;
    const internet_banking_id = reportData?.internet_banking_id || null;
    const employees = reportData?.employees || [];
    const hasIfscData = employees.length > 0 && employees[0].ifsc_code !== undefined;
    const totalNetPay = employees.reduce((sum, emp) => sum + Math.round(emp.net_pay || 0), 0);

    let textContent = '';
    
    // Internet banking ID line (use "-" if not available)
    textContent += `${internet_banking_id || '-'}\n`;
    
    // Formula-based formatting:
    // TEXT(account,"000000000000") & REPT(" ",4) & "INR" & LEFT(account,4) & REPT(" ",4) & "D/C" & REPT(" ",15-LEN(amount)) & amount & narration

    // Helper: zero-pad account to 12 digits (matches TEXT(A4,"000000000000"))
    const formatAccount12 = (accountNumber) => {
      if (!accountNumber) return '000000000000';
      const cleaned = cleanDisplayValue(accountNumber);
      return cleaned.padStart(12, '0').substring(0, 12);
    };

    // Helper: right-align amount in a 15-char field (matches REPT(" ",15-LEN(amount)) & amount)
    const formatAmount15 = (amount) => {
      const amtStr = String(amount);
      return amtStr.padStart(15, ' ');
    };

    // First line: Debit entry (total amount)
    const first4Digits = bank_account_number ? getFirst4Digits(bank_account_number) : '0000';
    const formattedBankAccount = formatAccount12(bank_account_number);
    const debitInrCode = `INR${first4Digits}`;
    const debitAmount = totalNetPay.toFixed(2);

    // Debit line: [12-digit account][4 spaces][INR+4digits][4 spaces][D][15-char right-aligned amount][narration]
    textContent += `${formattedBankAccount}    ${debitInrCode}    D${formatAmount15(debitAmount)}${debitNarration}\n`;
    
    // Credit entries for each employee - same column structure
    employees.forEach((emp) => {
      const empFirst4 = getFirst4Digits(emp.account_number);
      const empAccount = formatAccount12(emp.account_number);
      const creditInrCode = `INR${empFirst4}`;
      const creditAmount = Math.round(emp.net_pay || 0).toFixed(2);
      
      // Credit line: [12-digit account][4 spaces][INR+4digits][4 spaces][C][15-char right-aligned amount][narration]
      textContent += `${empAccount}    ${creditInrCode}    C${formatAmount15(creditAmount)}${creditNarration}\n`;
    });
    
    // Generate dynamic filename with counter
    const monthAbbr = month.substring(0, 3);
    const yearShort = String(year).slice(-2);
    const counter = uploadCounter ?? consumeUploadCounter(month, year);
    const filename = `${getOrdinalSuffix(counter)}upld${monthAbbr}${yearShort}Sal.txt`;
    
    // Create blob and download
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Get current date in format dd-mm-yyyy
  const getFormattedDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Download Excel file with IFSC data
  const downloadExcelFile = (reportData, month, year, creditNarration, debitNarration) => {
    const bank_account_number = reportData?.bank_account_number || null;
    const employees = reportData?.employees || [];

    // Prepare data for Excel
    const excelData = [];
    const numCols = 19;
    
    // Add column header row
    excelData.push([
      'PYMT_PROD_TYPE_CODE',
      'PYMT_MODE',
      'DEBIT_ACC_NO',
      'BNF_NAME',
      'BENE_ACC_NO',
      'BENE_IFSC',
      'AMOUNT',
      'DEBIT_NARR',
      'CREDIT_NARR',
      'MOBILE_NUM',
      'EMAIL_ID',
      'REMARK',
      'PYMT_DATE',
      'REF_NO',
      'ADDL_INFO1',
      'ADDL_INFO2',
      'ADDL_INFO3',
      'ADDL_INFO4',
      'LEI_NUMBER'
    ]);

    // Get formatted date for PYMT_DATE column
    const currentDate = getFormattedDate();

    // Add data rows
    employees.forEach((emp) => {
      // const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
      const fullName = emp?.full_name?.trim();
      excelData.push([
        'PAB_VENDOR',      // PYMT_PROD_TYPE_CODE
        'NEFT',            // PYMT_MODE
        bank_account_number || '', // DEBIT_ACC_NO
        fullName,          // BNF_NAME
        emp.account_number || '', // BENE_ACC_NO
        emp.ifsc_code || '', // BENE_IFSC
        Math.round(emp.net_pay || 0),  // AMOUNT
        debitNarration,    // DEBIT_NARR
        creditNarration,   // CREDIT_NARR
        emp.mobile_no || '', // MOBILE_NUM
        emp.email || '',   // EMAIL_ID
        '',                // REMARK
        currentDate,       // PYMT_DATE (dd-mm-yyyy format)
        '',                // REF_NO
        '',                // ADDL_INFO1
        '',                // ADDL_INFO2
        '',                // ADDL_INFO3
        '',                // ADDL_INFO4
        ''                 // LEI_NUMBER
      ]);
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Calculate column widths based on content
    const calculateColumnWidths = (data) => {
      const colWidths = [];
      const numCols = data[0]?.length || 19;
      
      for (let col = 0; col < numCols; col++) {
        let maxWidth = 0;
        
        data.forEach((row) => {
          if (row?.[col] !== undefined && row?.[col] !== null) {
            const cellValue = String(row[col]);
            maxWidth = Math.max(maxWidth, cellValue.length);
          }
        });

        // Keep columns readable (avoid "compact" look)
        const minByCol = {
          0: 16, // PYMT_PROD_TYPE_CODE
          1: 10, // PYMT_MODE
          2: 16, // DEBIT_ACC_NO
          3: 24, // BNF_NAME
          4: 18, // BENE_ACC_NO
          5: 14, // BENE_IFSC
          6: 12, // AMOUNT
          7: 18, // DEBIT_NARR
          8: 18, // CREDIT_NARR
          9: 14, // MOBILE_NUM
          10: 26, // EMAIL_ID
          11: 12, // REMARK
          12: 12, // PYMT_DATE
          13: 12, // REF_NO
          14: 12, // ADDL_INFO1
          15: 12, // ADDL_INFO2
          16: 12, // ADDL_INFO3
          17: 12, // ADDL_INFO4
          18: 12  // LEI_NUMBER
        };

        const minWidth = minByCol[col] ?? 12;
        // Add padding and cap extremely wide columns
        const padded = maxWidth + 2;
        const wch = Math.max(minWidth, Math.min(padded, 60));
        colWidths.push({ wch });
      }
      
      return colWidths;
    };

    // Set column widths dynamically based on content
    ws['!cols'] = calculateColumnWidths(excelData);

    // Common border style
    const borderStyle = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    };

    // Style the first 7 column headers and PYMT_DATE with red text color
    const redHeaderStyle = {
      font: { color: { rgb: "FF0000" }, bold: true },
      fill: { fgColor: { rgb: "5B9BD5" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: borderStyle
    };

    // Style remaining column headers with lighter blue background
    const blueHeaderStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "0B418E" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: borderStyle
    };

    // Apply red style to first 7 columns (A1-G1) and PYMT_DATE (M1)
    const redHeaderCells = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1', 'M1'];
    redHeaderCells.forEach(cell => {
      if (ws[cell]) {
        ws[cell].s = redHeaderStyle;
      }
    });

    // Apply blue style to remaining columns (H1-L1, N1-S1)
    const blueHeaderCells = ['H1', 'I1', 'J1', 'K1', 'L1', 'N1', 'O1', 'P1', 'Q1', 'R1', 'S1'];
    blueHeaderCells.forEach(cell => {
      if (ws[cell]) {
        ws[cell].s = blueHeaderStyle;
      }
    });

    // Apply data cell styles
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let row = 1; row <= range.e.r; row++) {
      for (let col = 0; col < 19; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            border: borderStyle,
            alignment: { vertical: "center", horizontal: "left" }
          };
          // AMOUNT column (G) with comma-separated format
          if (col === 6) {
            ws[cellAddress].z = '#,##0';
          }
        }
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Bank Statement');

    // Generate filename
    const filename = `${getShortCompanyName(globalCompanyName || reportData?.company_name) || 'Report'}ReportforBankStatement${month.substring(0, 3)}${year}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);
  };

  // Helper: convert Buffer-like values to readable string
  const toReadableString = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (value?.type === 'Buffer' && Array.isArray(value?.data)) {
      try {
        return new TextDecoder('utf-8').decode(new Uint8Array(value.data));
      } catch (e) {
        return String.fromCharCode(...value.data);
      }
    }
    return String(value);
  };

  const buildEmployeePanMap = (masterEmployees) => {
    const byId = new Map();
    const byCode = new Map();
    (masterEmployees || []).forEach((emp) => {
      const pan = toReadableString(emp?.pan_card_no || '').trim();
      if (emp?.employee_id != null) byId.set(emp.employee_id, pan);
      const code = toReadableString(emp?.employee_code || '').trim();
      if (code) byCode.set(code, pan);
    });
    return { byId, byCode };
  };

  const getPanForEmployee = (emp, empMeta, panMap) => {
    const panById = panMap.byId.get(emp?.employee_id);
    if (panById) return panById;
    const employeeCode = (
      toReadableString(emp?.employee_code || emp?.employeeCode || emp?.emp_code).trim()
      || toReadableString(empMeta?.employeeCode).trim()
    );
    return panMap.byCode.get(employeeCode) || '';
  };

  // Download ICICI summary Excel file (Sr No, Employee Code, PAN, Name, Net Pay)
  const downloadIciciSummaryExcel = async (reportData, month, year, uploadCounter) => {
    const employees = reportData?.employees || [];
    let panMap = { byId: new Map(), byCode: new Map() };
    try {
      const masterResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/employee-master/view-employees`
      );
      panMap = buildEmployeePanMap(masterResponse.data);
    } catch (error) {
      console.error('Failed to fetch employee PAN details:', error);
    }

    const employeeMetaById = new Map(
      empDetails
        .flat()
        .map((emp) => [
          emp?.employee_id,
          {
            employeeCode: toReadableString(emp?.employee_code || emp?.employeeCode || emp?.emp_code || '').trim(),
            employeeName: toReadableString(emp?.name || '').trim()
          }
        ])
    );
    const wb = XLSX.utils.book_new();
    const wsData = [];
    const numCols = 5;
    let totalNetPay = 0;

    // Company header rows (same pattern as other reports)
    wsData.push([reportData?.company_name || globalCompanyName || '', ...Array(numCols - 1).fill('')]);
    wsData.push([globalAddressLine1 || '', ...Array(numCols - 1).fill('')]);
    wsData.push([globalAddressLine2 || '', ...Array(numCols - 1).fill('')]);
    wsData.push([`ICICI Salary Report for ${month} ${year}`, ...Array(numCols - 1).fill('')]);

    // Column header row
    wsData.push(['Sr No', 'Employee Code', 'PAN Number', 'Employee Name', 'Net Pay']);

    employees.forEach((emp, index) => {
      const empMeta = employeeMetaById.get(emp?.employee_id) || {};
      const fullName = (
        toReadableString(emp?.full_name).trim()
        || toReadableString(emp?.name).trim()
        || toReadableString(empMeta?.employeeName).trim()
        || `${toReadableString(emp?.first_name).trim()} ${toReadableString(emp?.last_name).trim()}`.trim()
      );
      const employeeCode = (
        toReadableString(emp?.employee_code || emp?.employeeCode || emp?.emp_code).trim()
        || toReadableString(empMeta?.employeeCode).trim()
        || toReadableString(emp?.employee_id).trim()
      );
      const netPay = Math.round(emp?.net_pay || 0);
      totalNetPay += netPay;

      wsData.push([
        index + 1,
        employeeCode,
        getPanForEmployee(emp, empMeta, panMap),
        fullName || 'N/A',
        netPay
      ]);
    });

    wsData.push(['', '', '', 'Grand Total', totalNetPay]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws['!cols'] = [{ wch: 8 }, { wch: 16 }, { wch: 14 }, { wch: 28 }, { wch: 14 }];

    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: numCols - 1 } }
    ];

    const borderStyle = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    };

    const companyNameStyle = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: borderStyle
    };

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "5B9BD5" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: borderStyle
    };

    const dataStyle = {
      border: borderStyle,
      alignment: { vertical: "center" }
    };

    for (let row = 0; row <= 3; row++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
      if (ws[cellAddress]) ws[cellAddress].s = companyNameStyle;
      for (let col = 1; col < numCols; col++) {
        const emptyCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[emptyCellAddress]) delete ws[emptyCellAddress];
      }
    }

    for (let col = 0; col < numCols; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 4, c: col });
      if (ws[cellAddress]) ws[cellAddress].s = headerStyle;
    }

    const grandTotalRowIndex = 5 + employees.length;
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let row = 5; row <= range.e.r; row++) {
      const isGrandTotalRow = row === grandTotalRowIndex;
      for (let col = 0; col < numCols; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            ...dataStyle,
            font: isGrandTotalRow ? { bold: true } : undefined,
            alignment: {
              ...dataStyle.alignment,
              horizontal: col === 4 ? 'right' : (col === 3 ? 'left' : 'center')
            }
          };
        }
      }
      const amountCell = ws[XLSX.utils.encode_cell({ r: row, c: 4 })];
      if (amountCell) amountCell.z = '#,##0';
    }

    XLSX.utils.book_append_sheet(wb, ws, 'ICICI Report');
    const counter = uploadCounter ?? consumeUploadCounter(month, year);
    const companyName = reportData?.company_name || globalCompanyName || '';
    XLSX.writeFile(wb, getIciciSalaryUploadExcelFilename(companyName, month, year, counter));
  };

  // Fetch employee data
  const fetchEmployeeData = async (month, year) => {
    try {
      setLoaderMessage('Loading employee data...');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/report/get-month-wise-employees/${month}/${year}`
      );
      console.log('API Response:', response.data);
      // Handle new response format: { companyName: "...", data: [[{employee_id, employee_code, name, net_pay}, ...]] }
      // Keep the nested array structure for multiple tables
      let employeeData = [];
      
      // Check for new response format with companyName and data fields
      if (response.data && response.data.companyName && response.data.data) {
        // New format: { companyName: "...", data: [[...]] }
        const dataArray = response.data.data;
        if (Array.isArray(dataArray)) {
          if (dataArray.length > 0 && Array.isArray(dataArray[0])) {
            // It's nested: [[...], [...]]
            employeeData = dataArray.filter(arr => Array.isArray(arr) && arr.length > 0);
          } else {
            // It's a flat array, wrap it
            employeeData = [dataArray];
          }
        }
      } else if (Array.isArray(response.data)) {
        // Old format: direct array
        // Check if it's nested array structure
        if (response.data.length > 0 && Array.isArray(response.data[0])) {
          // It's already nested: [[...], [...]]
          employeeData = response.data.filter(arr => Array.isArray(arr) && arr.length > 0);
        } else {
          // It's a flat array, wrap it in another array
          employeeData = [response.data];
        }
      }
      console.log('Processed Employee Data:', employeeData);
      console.log('Number of tables:', employeeData.length);
      
      setEmpDetails(employeeData);
      setSelectedEmployees([]); // Reset selections when new data is loaded
      setActiveEmployeeTab('icici');
      setGeneratedTables(new Set()); // Reset generated tables when new data is loaded
      setGeneratedEmployeeIds(new Set()); // Reset generated employee IDs when new data is loaded
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setNotify({
          isOpen: true,
          message: 'Failed to fetch employee data',
          type: 'error'
        });
      }
    }
  };

  // Clear localStorage counter on component mount (page refresh)
  useEffect(() => {
    // Clear all bank_statement_counter keys from localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('bank_statement_counter_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }, []); // Only on mount

  // Clear localStorage counter on component unmount (navigation away)
  useEffect(() => {
    return () => {
      // Clear all bank_statement_counter keys from localStorage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('bank_statement_counter_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    };
  }, []); // Only on unmount

  // Call API when component mounts
  useEffect(() => {
    fetchEmployeeData(values.month, values.year);
  }, []); // Only on mount

  // Call API when month or year changes
  useEffect(() => {
    if (values.month && values.year) {
      fetchEmployeeData(values.month, values.year);
    }
  }, [values.month, values.year]); // When month or year changes

  const validate = () => {
    let temp = {}
    temp.month = values.month.length != 0 ? "" : "This field is required."
    temp.year = values.year.length != 0 ? "" : "This field is required."

    setErrors({
      ...temp
    })

    return Object.values(temp).every(x => x == "")
  }


  function handleInputChange(e){
    const {name, value} = e.target;
    setValues({
      ...values,
      [name]: value
    })
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  }

  // Calculate total salary of selected employees across all tables (excluding generated/disabled records)
  const calculateTotalSalary = (selectedIds) => {
    let total = 0;
    empDetails.forEach(tableData => {
      tableData.forEach(emp => {
        // Only count if employee is selected AND not already generated/disabled
        if (selectedIds.includes(emp.employee_id) && !generatedEmployeeIds.has(emp.employee_id)) {
          total += Math.round(emp.net_pay || 0);
        }
      });
    });
    return total;
  };

  // Get all employees from all tables as a flat array
  const getAllEmployees = () => {
    return empDetails.flat();
  };

  // Handle checkbox selection for individual employee
  const handleSelectEmployee = (employeeId, shouldEnforceLimit = true) => {
    // Don't allow selection of generated/disabled employees
    if (generatedEmployeeIds.has(employeeId)) {
      return;
    }
    
    setSelectedEmployees(prev => {
      let newSelection;
      if (prev.includes(employeeId)) {
        newSelection = prev.filter(id => id !== employeeId);
      } else {
        newSelection = [...prev, employeeId];
      }
      
      // Check if total exceeds 10 lakhs (excluding generated employees)
      const totalSalary = calculateTotalSalary(newSelection);
      if (shouldEnforceLimit && totalSalary > 1000000) {
        setNotify({
          isOpen: true,
          message: 'The sum of salary of selected employees cannot exceed 10 lakhs!',
          type: 'error'
        });
        return prev; // Don't update selection
      }
      
      return newSelection;
    });
  };

  // Handle select all checkbox for a specific table
  const handleSelectAll = (tableIndex, event) => {
    if (event.target.checked) {
      const tableEmployeeIds = empDetails[tableIndex].map(emp => emp.employee_id);
      const newSelection = [...selectedEmployees, ...tableEmployeeIds];
      const totalSalary = calculateTotalSalary(newSelection);
      
      if (totalSalary > 1000000) {
        setNotify({
          isOpen: true,
          message: 'The sum of salary of selected employees cannot exceed 10 lakhs!',
          type: 'error'
        });
        return;
      }
      
      setSelectedEmployees(newSelection);
    } else {
      // Unselect all employees from this table
      const tableEmployeeIds = empDetails[tableIndex].map(emp => emp.employee_id);
      setSelectedEmployees(prev => prev.filter(id => !tableEmployeeIds.includes(id)));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
      if(validate()){
      axios.post(`${process.env.REACT_APP_API_BASE_URL}/report/emp-details`, values)
      .then((response=>{
        setEmpDetails(response.data);
        navigate('/bank-statement-report', {state: {empDetails:empDetails}});
      }))
      .catch((e)=>{
        if(e.response.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error'
          });
          setTimeout(() => {
            navigate('/');
          },1000);
        }
      })

    }
  }

  const handleGenerate = () => {
    const totalSalary = calculateTotalSalary(selectedEmployees);
    
    if (totalSalary > 1000000) {
      setNotify({
        isOpen: true,
        message: 'The sum of salary of selected employees cannot exceed 10 lakhs!',
        type: 'error'
      });
      return;
    }

    // Open modal for narration input
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCreditNarration('');
    setDebitNarration('');
  };

  const handleSave = async () => {
    if (!creditNarration.trim() || !debitNarration.trim()) {
      setNotify({
        isOpen: true,
        message: 'Please fill in both credit and debit narration!',
        type: 'error'
      });
      return;
    }

    try {
      setLoaderMessage('Generating bank statement...');
      const payload = {
        selectedMonth: values.month,
        selectedYear: values.year,
        employee_ids: selectedEmployees,
        creditNarration: creditNarration,
        debitNarration: debitNarration
      };

      // Call API endpoint
      const apiEndpoint = `${process.env.REACT_APP_API_BASE_URL}/report/get-salary-data`;

      console.log('API Endpoint:', apiEndpoint);
      console.log('Payload:', payload);

      const response = await axios.post(apiEndpoint, payload);
      console.log('Response:', response.data);

      // Check if response has is_ifsc key
      const isIfsc = response.data?.is_ifsc;

      // Download files based on is_ifsc flag
      if (isIfsc === false) {
        // For ICICI (non-IFSC), download both text and summary Excel
        const uploadCounter = consumeUploadCounter(values.month, values.year);
        downloadNotepadFile(response.data, values.month, values.year, creditNarration, debitNarration, uploadCounter);
        await downloadIciciSummaryExcel(response.data, values.month, values.year, uploadCounter);
      } else if (isIfsc === true) {
        // Download Excel file for IFSC records
        downloadExcelFile(response.data, values.month, values.year, creditNarration, debitNarration);
      } else {
        // If response structure has both types (check for separate arrays or mixed data)
        // Check if employees array has mixed is_ifsc values
        const employees = response.data?.employees || [];
        const nonIfscEmployees = employees.filter(emp => emp.is_ifsc === false);
        const ifscEmployees = employees.filter(emp => emp.is_ifsc === true);

        // Download both files if both types exist
        if (nonIfscEmployees.length > 0) {
          const nonIfscData = { ...response.data, employees: nonIfscEmployees };
          // For ICICI (non-IFSC), download both text and summary Excel
          const uploadCounter = consumeUploadCounter(values.month, values.year);
          downloadNotepadFile(nonIfscData, values.month, values.year, creditNarration, debitNarration, uploadCounter);
          await downloadIciciSummaryExcel(nonIfscData, values.month, values.year, uploadCounter);
        }
        if (ifscEmployees.length > 0) {
          const ifscData = { ...response.data, employees: ifscEmployees };
          downloadExcelFile(ifscData, values.month, values.year, creditNarration, debitNarration);
        }
      }

      // Mark selected employees as generated to disable them
      setGeneratedEmployeeIds(prev => new Set([...prev, ...selectedEmployees]));
      
      // Clear selection after generation (generated employees are now disabled)
      setSelectedEmployees([]);

      // Mark table(s) as generated to disable the button(s)
      if (currentTableIndex !== null) {
        setGeneratedTables(prev => new Set([...prev, currentTableIndex]));
      }

      // Show success notification
      setNotify({
        isOpen: true,
        message: 'Report generated and downloaded successfully!',
        type: 'success'
      });

      handleCloseModal();
      setCurrentTableIndex(null);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setNotify({
          isOpen: true,
          message: 'Failed to generate report. Please try again.',
          type: 'error'
        });
      }
    }
  };

  return(
    <Box sx={{ display: "flex", justifyContent: "center", margin: isEmbedded ? "0" : "10px", padding: isEmbedded ? "10px 20px" : "10px", fontWeight: "500px" }}>
      <Paper elevation={isEmbedded ? 0 : 5} sx={{ width: isEmbedded ? "100%" : '100%', maxWidth: '1400px' }}>
        {!isEmbedded && (
          <Grid className="form-title" container spacing={2}>
            <Grid item>
              <Typography variant="h4" sx={{ textAlign: "center", margin: 5, display: "flex", alignItems: "center", gap: 1, marginBottom: "8px", marginTop: "4px" }}>
                <Summarize sx={{ width: 40, height: 40 }} />
                Bank Statement Report
              </Typography>
            </Grid>
          </Grid>
        )}
        <form className="classes.root" autoComplete="off" onSubmit={handleSubmit}>
          <Box sx={{ margin: isEmbedded ? "15px 30px" : "20px", marginTop: '0px' }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 2, mx: "10px" }}>
                <FormControl variant="outlined" sx={{ minWidth: 200 }} {...(errors.month && { error: true })}>
                <InputLabel><RequiredLabel label="Month" /></InputLabel>
                  <Select label="Month" name="month" size="small" value={values.month} onChange={handleInputChange}>
                  <MenuItem value="">None</MenuItem>
                    {monthOptions.map(item => (<MenuItem key={item.id} value={item.title}>{item.title}</MenuItem>))}
                </Select>
                  {errors.month && <FormHelperText>{errors.month}</FormHelperText>}
              </FormControl>
                <FormControl variant="outlined" sx={{ minWidth: 200 }} {...(errors.year && { error: true })}>
                <InputLabel><RequiredLabel label="Year" /></InputLabel>
                  <Select label="Year" name="year" size="small" value={values.year} onChange={handleInputChange}>
                  <MenuItem value="">None</MenuItem>
                    {yearOptions.map(item => (<MenuItem key={item.id} value={item.title}>{item.title}</MenuItem>))}
                </Select>
                  {errors.year && <FormHelperText>{errors.year}</FormHelperText>}
              </FormControl>
            </Box>
            </Stack>
          </Box>
        </form>

        {/* Employee Data Table - same design as Income Tax */}
      {empDetails.length > 0 && (
          <Box sx={{ margin: "20px", marginTop: '0px' }}>
            <Stack spacing={2}>
              <Box sx={{ width: '100%', margin: "10px", background: '#ffffff', borderRadius: 2, border: '1px solid #e9ecef', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', overflow: 'hidden' }}>
            {(() => {
                  const allEmployees = empDetails.flat();
                  const iciciEmployees = allEmployees.filter(emp => emp.is_ifsc === false);
                  const nonIciciEmployees = allEmployees.filter(emp => emp.is_ifsc === true);
                  const iciciEmployeeIds = iciciEmployees.map(emp => emp.employee_id);
                  const nonIciciEmployeeIds = nonIciciEmployees.map(emp => emp.employee_id);
                  const getGroupTotalSalary = (employees) => employees.reduce((sum, emp) => sum + Math.round(emp.net_pay || 0), 0);
                  const selectedIciciIds = selectedEmployees.filter(id => iciciEmployeeIds.includes(id));
                  const selectedNonIciciIds = selectedEmployees.filter(id => nonIciciEmployeeIds.includes(id));
                  const selectedIciciCount = selectedIciciIds.length;
                  const selectedNonIciciCount = selectedNonIciciIds.length;
                  const selectedIciciSalary = calculateTotalSalary(selectedIciciIds);
                  const selectedNonIciciSalary = calculateTotalSalary(selectedNonIciciIds);
                  const currentEmployees = activeEmployeeTab === 'icici' ? iciciEmployees : nonIciciEmployees;
                  const currentEmployeeIds = currentEmployees.map(emp => emp.employee_id);
                  const allTableSelected = currentEmployeeIds.length > 0 && currentEmployeeIds.every(id => selectedEmployees.includes(id));
                  const someTableSelected = currentEmployeeIds.some(id => selectedEmployees.includes(id));
              const handleSelectAll = (event, employeeIds, shouldEnforceLimit) => {
                if (event.target.checked) {
                        const availableEmployeeIds = employeeIds.filter(id => !generatedEmployeeIds.has(id));
                        const newSelection = Array.from(new Set([...selectedEmployees, ...availableEmployeeIds]));
                  const totalSalary = calculateTotalSalary(newSelection);
                  if (shouldEnforceLimit && totalSalary > 1000000) {
                        setNotify({ isOpen: true, message: 'The sum of salary of selected employees cannot exceed 10 lakhs!', type: 'error' });
                    return;
                  }
                        setSelectedEmployees(newSelection);
                } else {
                        setSelectedEmployees(prev => prev.filter(id => !employeeIds.includes(id)));
                }
              };
              const handleTableGenerate = (employeeIds, shouldEnforceLimit) => {
                    const selectedInTab = selectedEmployees.filter(id => employeeIds.includes(id));
                if (selectedInTab.length === 0) {
                      setNotify({ isOpen: true, message: 'Please select at least one employee!', type: 'error' });
                  return;
                }
                const totalSalary = calculateTotalSalary(selectedInTab);
                if (shouldEnforceLimit && totalSalary > 1000000) {
                      setNotify({ isOpen: true, message: 'The sum of salary of selected employees cannot exceed 10 lakhs!', type: 'error' });
                  return;
                }
                    setSelectedEmployees(selectedInTab);
                setCurrentTableIndex(0);
                setOpenModal(true);
              };
              return (
                <>
                      <Box sx={{ flexShrink: 0, backgroundColor: '#ffffff', px: 2, pt: 2, pb: 1, borderBottom: '2px solid #dee2e6', boxShadow: '0 2px 6px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', flexWrap: 'wrap', gap: 2 }}>
                        {/* ICICI Summary Box */}
                        <Box sx={{ flex: 1, minWidth: '200px', border: '1px solid #b8d4f0', borderRadius: 1, p: 1.5, backgroundColor: '#eaf4fb' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f4e79' }}>
                            ICICI — {iciciEmployees.length} Employees
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, color: '#495057', fontWeight: 600 }}>
                            Total Salary: ₹{getGroupTotalSalary(iciciEmployees).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700, color: selectedIciciSalary > 1000000 ? '#d32f2f' : '#1b7a3e' }}>
                            Selected: {selectedIciciCount} &nbsp;|&nbsp; ₹{selectedIciciSalary.toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="caption" sx={{ mt: 0.25, display: 'block', color: selectedIciciSalary > 1000000 ? '#d32f2f' : '#6c757d', fontWeight: 600 }}>
                            Limit: ₹10,00,000
                          </Typography>
                        </Box>
                        {/* Non-ICICI Summary Box */}
                        <Box sx={{ flex: 1, minWidth: '200px', border: '1px solid #c3e6cb', borderRadius: 1, p: 1.5, backgroundColor: '#f0fbf4' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f4e79' }}>
                            Non-ICICI — {nonIciciEmployees.length} Employees
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, color: '#495057', fontWeight: 600 }}>
                            Total Salary: ₹{getGroupTotalSalary(nonIciciEmployees).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700, color: '#1b7a3e' }}>
                            Selected: {selectedNonIciciCount} &nbsp;|&nbsp; ₹{selectedNonIciciSalary.toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                        {/* Combined Total Box */}
                        <Box sx={{ flex: 1, minWidth: '200px', border: '2px solid #f0ad4e', borderRadius: 1, p: 1.5, backgroundColor: '#fffbf0' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7d4e00' }}>
                            Grand Total — {allEmployees.length} Employees
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, color: '#495057', fontWeight: 600 }}>
                            Total Salary: ₹{(getGroupTotalSalary(iciciEmployees) + getGroupTotalSalary(nonIciciEmployees)).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700, color: '#7d4e00' }}>
                            Selected: {selectedIciciCount + selectedNonIciciCount} &nbsp;|&nbsp; ₹{(selectedIciciSalary + selectedNonIciciSalary).toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ flexShrink: 0, px: 2, pt: 1, borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Tabs
                          value={activeEmployeeTab}
                          onChange={(event, newValue) => setActiveEmployeeTab(newValue)}
                          sx={{ minHeight: '40px', '& .MuiTab-root': { minHeight: '40px', textTransform: 'none', fontWeight: 600 } }}
                        >
                          <Tab label={`ICICI (${iciciEmployees.length})`} value="icici" />
                          <Tab label={`Non-ICICI (${nonIciciEmployees.length})`} value="nonIcici" />
                        </Tabs>
                        <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500 }}>
                          Showing: {currentEmployees.length} employees
                        </Typography>
                      </Box>
                      <div style={{ flex: 1, width: '100%', overflow: 'auto', border: '1px solid #dee2e6', backgroundColor: '#fff' }}>
                        <Table size="small" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                          <TableHead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#212529', color: 'white', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.2)' }}>
                            <TableRow>
                              <TableCell padding="checkbox" sx={{ width: '48px', color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>
                                <Checkbox size="small" checked={allTableSelected} indeterminate={someTableSelected && !allTableSelected} onChange={(event) => handleSelectAll(event, currentEmployeeIds, activeEmployeeTab === 'icici')} sx={{ color: 'rgba(255,255,255,0.8)', '&.Mui-checked': { color: 'white' }} } />
                          </TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>Employee Code</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px', textAlign: 'right' }}>Net Pay</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentEmployees.map((employee) => {
                          const isGenerated = generatedEmployeeIds.has(employee.employee_id);
                          return (
                                <TableRow key={employee.employee_id} hover sx={{ backgroundColor: isGenerated ? '#f8f9fa' : 'white', opacity: isGenerated ? 0.7 : 1 }}>
                                  <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>
                                    <Checkbox size="small" checked={selectedEmployees.includes(employee.employee_id)} onChange={() => handleSelectEmployee(employee.employee_id, employee.is_ifsc === false)} disabled={isGenerated} />
                              </TableCell>
                                  <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px', color: isGenerated ? '#999' : 'inherit' }}>{employee.employee_code}</TableCell>
                                  <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px', color: isGenerated ? '#999' : 'inherit' }}>{employee.name}</TableCell>
                                  <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px', textAlign: 'right', color: isGenerated ? '#999' : 'inherit' }}>₹{Math.round(employee.net_pay || 0).toLocaleString('en-IN')}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                      </div>
                      <Box sx={{ flexShrink: 0, backgroundColor: '#ffffff', borderTop: '2px solid #dee2e6', boxShadow: '0 -2px 6px rgba(0,0,0,0.08)', display: "flex", justifyContent: "center", py: 1.5 }}>
                        <Button color="primary" variant="contained" size="large" onClick={() => handleTableGenerate(currentEmployeeIds, activeEmployeeTab === 'icici')} disabled={selectedEmployees.filter(id => currentEmployeeIds.includes(id)).length === 0}>
                          Generate
                        </Button>
                      </Box>
                </>
              );
            })()}
          </Box>
            </Stack>
          </Box>
      )}

      </Paper>
      <Notification
        notify={notify}
        setNotify={setNotify} 
      />

      {/* Modal for Credit and Debit Narration */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{fontWeight: "bold"}}>
            Add Narration Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{display: "flex", flexDirection: "column", gap: 3, marginTop: 2}}>
            <TextField
              label="Credit Narration"
              variant="outlined"
              fullWidth
              value={creditNarration}
              onChange={(e) => setCreditNarration(e.target.value)}
              placeholder="Enter credit narration"
              multiline
              rows={2}
            />
            <TextField
              label="Debit Narration"
              variant="outlined"
              fullWidth
              value={debitNarration}
              onChange={(e) => setDebitNarration(e.target.value)}
              placeholder="Enter debit narration"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{padding: "16px 24px"}}>
          <Button 
            onClick={handleCloseModal} 
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default BankStatementReport;